/**
 * Paddle.js, loaded on demand (PLAN-LAUNCH B3).
 *
 * Everything a browser needs to take money, in one file and with no new
 * dependency. `@paddle/paddle-js` exists and does roughly this, but what it adds
 * over the twelve lines below is a package to keep in step with a CDN script
 * that is versioned by URL anyway — so this module types the two globals it
 * touches and stops there. The same call the repo makes about better-auth's
 * client (see `auth/client.ts`).
 *
 * ## Why it is lazy
 *
 * The script is ~100 KB of third-party JavaScript that matters to the small
 * fraction of sessions that open the upgrade dialog and press a button. Loading
 * it on boot would put a payments CDN on the critical path of an app whose
 * whole promise is that it works offline. So nothing is fetched until a
 * purchase is actually attempted, and the fetch is cached in a module-level
 * promise so a user who opens the dialog twice loads it once.
 *
 * ## Why the checkout is a promise over a global callback
 *
 * `Paddle.Initialize` takes exactly one `eventCallback` for the lifetime of the
 * page, and `Paddle.Checkout.open` returns nothing — the outcome arrives as an
 * event some seconds later, or never, because closing the overlay is a valid
 * end to the interaction. {@link openCheckout} bridges that to a promise that
 * settles once, on whichever of `checkout.completed` and `checkout.closed`
 * arrives first: the caller gets `completed` and starts polling for the plan, or
 * `closed` and does nothing at all.
 *
 * ## No DOM, no purchase
 *
 * Every entry point checks for `document` before touching it, so importing this
 * module in a node test (or in any future server render) is inert rather than a
 * crash. Tests of the dialog mock this module; nothing in the suite loads the
 * real script.
 */

/** How the overlay ended. `closed` includes "changed their mind". */
export type CheckoutOutcome = 'completed' | 'closed';

/** The Paddle.js surface this app uses. Deliberately no wider than that. */
interface PaddleGlobal {
  Initialize(options: { token: string; eventCallback?: (event: { name?: string }) => void }): void;
  Checkout: {
    open(options: CheckoutOpenOptions): void;
  };
}

/**
 * A new checkout names its items; a resumed one names only the transaction,
 * which already knows its items, its customer and its `custom_data`.
 */
type CheckoutOpenOptions = {
  settings?: {
    displayMode?: 'overlay' | 'inline';
    theme?: 'light' | 'dark';
    allowLogout?: boolean;
  };
} & (
  | {
      items: readonly { priceId: string; quantity: number }[];
      customer?: { email: string };
      customData?: Record<string, string>;
    }
  | { transactionId: string }
);

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

/** Paddle's v2 bundle. Versioned by URL, which is why nothing pins it in `package.json`. */
const PADDLE_SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

/**
 * The one in-flight-or-finished load.
 *
 * Module-level, so the second call to {@link loadPaddle} awaits the first
 * rather than injecting a second `<script>` — and so a failed load is retried
 * (the promise is cleared on rejection) instead of poisoning every later
 * attempt.
 */
let loading: Promise<PaddleGlobal> | null = null;

/**
 * Whoever is waiting on the currently open checkout.
 *
 * There is one `eventCallback` per page and it is installed once, at
 * `Initialize` time, so the callback cannot close over a particular checkout.
 * It dispatches here instead. Cleared as soon as it settles, which is what makes
 * a second purchase attempt in the same session behave like the first.
 */
let pending: ((outcome: CheckoutOutcome) => void) | null = null;

/** Whoever asked to hear that the current overlay has actually rendered. */
let opened: (() => void) | null = null;

/** Whether this environment can host an overlay at all. */
function hasDom(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}

/**
 * Load and initialise Paddle.js, once.
 *
 * @param token - The deployment's client-side token, from `/api/config`.
 * @throws When there is no DOM, or the script cannot be fetched — a blocked CDN,
 *   an ad blocker, an offline tab. The caller turns that into a toast; it must
 *   never look like a failed *payment*.
 */
export function loadPaddle(token: string): Promise<PaddleGlobal> {
  if (loading !== null) return loading;
  loading = initialize(token).catch((error: unknown) => {
    // Cleared so a user who was offline when they first pressed the button can
    // press it again once they are not.
    loading = null;
    throw error;
  });
  return loading;
}

async function initialize(token: string): Promise<PaddleGlobal> {
  if (!hasDom()) throw new Error('Paddle needs a browser.');
  await injectScript();
  const paddle = window.Paddle;
  if (paddle === undefined) throw new Error('Paddle.js loaded but did not register itself.');
  paddle.Initialize({
    token,
    eventCallback: (event) => {
      if (event.name === 'checkout.completed') settle('completed');
      else if (event.name === 'checkout.closed') settle('closed');
      else if (event.name === 'checkout.loaded') opened?.();
    },
  });
  return paddle;
}

function settle(outcome: CheckoutOutcome): void {
  const resolve = pending;
  pending = null;
  opened = null;
  resolve?.(outcome);
}

function injectScript(): Promise<void> {
  // A hot reload re-runs this module with the script already in the document.
  if (window.Paddle !== undefined) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    // Without this, a throw inside Paddle's bundle reaches `window.onerror` as
    // the browser's sanitised `"Script error."` with no message, no file and no
    // stack, because the script came from another origin. Checkout is the one
    // flow where an unreadable error costs money, so the request is made with
    // CORS and the error arrives legible. Safe to ask for: the CDN answers an
    // `Origin` with `access-control-allow-origin: *`, so the script still loads.
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Could not load Paddle.js.')));
    document.head.append(script);
  });
}

/** Everything one purchase needs. */
export interface CheckoutRequest {
  /** `AuthConfig.billing.clientToken`. */
  readonly token: string;
  /** The Paddle price id for the tier *and billing period* the user chose. */
  readonly priceId: string;
  /** Prefilled in the overlay so a signed-in user does not retype it. */
  readonly email?: string;
  /**
   * The account to grant the plan to.
   *
   * This is the link between a payment and an entitlement: Paddle echoes it back
   * on every webhook for the resulting subscription, and `worker/billing.ts`
   * reads it out of `custom_data.userId` to decide whose `entitlement` row to
   * write. Nothing else ties the two systems together, so a checkout opened
   * without it produces a paid subscription with no account attached — which is
   * why the caller must be signed in before the button is offered.
   */
  readonly userId: string;
  /**
   * Which of Paddle's two overlay themes to open.
   *
   * Defaults to whatever the app is currently rendering — see
   * {@link currentTheme}. The app has a real dark mode (`ThemeProvider`, and a
   * `.dark` block of tokens in `styles.css`), so a hardcoded light overlay would
   * fire a full-screen white panel at somebody who deliberately chose dark, at
   * the single moment in the product where confidence matters most.
   */
  readonly theme?: 'light' | 'dark';
  /**
   * Called once, when the overlay has actually rendered (`checkout.loaded`).
   *
   * The gap between pressing a buy button and this firing is a script load, and
   * the caller wants different chrome on each side of it: its own spinner while
   * Paddle is being fetched, and *nothing of its own* once Paddle's panel is up
   * — an app modal left open under the overlay swallows the overlay's close
   * button as an outside-click on itself.
   */
  readonly onOpened?: () => void;
}

/**
 * What the app is rendering right now, read off the document.
 *
 * `<html class="dark">` is the app's own source of truth for the resolved theme:
 * `ThemeProvider` owns that class, and an inline script in `index.html` sets it
 * before first paint. Reading it here rather than taking `useTheme()` in the
 * dialog is deliberate — `useTheme` throws outside its provider, and the upgrade
 * dialog is a component that must keep rendering in isolation (its own test
 * mounts it with no providers at all). One `classList.contains` has no such
 * requirement and cannot be wrong, because it is reading the very thing the
 * provider writes.
 */
function currentTheme(): 'light' | 'dark' {
  if (!hasDom()) return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Open the overlay checkout and wait for it to end.
 *
 * Resolves `completed` the moment Paddle says the payment went through, which is
 * *before* the webhook has landed and therefore before the plan has actually
 * changed — the caller polls `/api/me` for that. Resolves `closed` when the user
 * dismissed the overlay, which is not an error and gets no message.
 *
 * @throws When Paddle.js cannot be loaded or initialised. A failure to *reach*
 *   the payment provider, never a failure to pay.
 */
export async function openCheckout(request: CheckoutRequest): Promise<CheckoutOutcome> {
  return await openOverlay(request.token, request.theme, request.onOpened, {
    items: [{ priceId: request.priceId, quantity: 1 }],
    ...(request.email === undefined ? {} : { customer: { email: request.email } }),
    customData: { userId: request.userId },
  });
}

/** A resumed checkout: the transaction from a recovery email, nothing else. */
export interface RecoveryCheckoutRequest {
  /** `AuthConfig.billing.clientToken`. */
  readonly token: string;
  /**
   * The `_ptxn` transaction id from the recovery link. The transaction was
   * created by an earlier {@link openCheckout}, so it already carries the
   * items, the customer email and — critically — the `custom_data.userId` the
   * webhook needs. Nothing has to be signed in on *this* device for the right
   * account to be granted the plan.
   */
  readonly transactionId: string;
  readonly theme?: 'light' | 'dark';
  readonly onOpened?: () => void;
}

/**
 * Reopen the overlay for an existing transaction — a Paddle recovery email's
 * `_ptxn` link. Same contract as {@link openCheckout} in every other respect.
 */
export async function openRecoveryCheckout(
  request: RecoveryCheckoutRequest,
): Promise<CheckoutOutcome> {
  return await openOverlay(request.token, request.theme, request.onOpened, {
    transactionId: request.transactionId,
  });
}

async function openOverlay(
  token: string,
  theme: 'light' | 'dark' | undefined,
  onOpened: (() => void) | undefined,
  options: CheckoutOpenOptions,
): Promise<CheckoutOutcome> {
  const paddle = await loadPaddle(token);
  // An overlay that is somehow already open loses its waiter rather than
  // stranding it: the old promise resolves as closed and the new one takes over.
  settle('closed');
  return await new Promise<CheckoutOutcome>((resolve) => {
    pending = resolve;
    opened = onOpened ?? null;
    paddle.Checkout.open({
      ...options,
      settings: {
        displayMode: 'overlay',
        theme: theme ?? currentTheme(),
        // The overlay must not offer to sign the buyer out of *Paddle's* account
        // and start again as somebody else: the checkout is already bound to
        // this app's signed-in user by `customData`, and a mismatch between the
        // two is exactly the state the webhook cannot resolve.
        allowLogout: false,
      },
    });
  });
}

/**
 * The transaction id a Paddle recovery email put in the URL, or `null` on the
 * overwhelming majority of boots that did not start from one.
 *
 * Paddle.js would find this itself as it initialises — that is the mechanism
 * the recovery email is built on — but this app only loads Paddle.js when a
 * purchase is attempted, so `components/paddle-recovery.tsx` does the looking
 * and the reopening.
 */
export function recoveryTransactionId(): string | null {
  if (!hasDom()) return null;
  return new URLSearchParams(window.location.search).get('_ptxn');
}

/**
 * Take `_ptxn` and `_paction` back out of the address bar.
 *
 * Called once the overlay has actually answered (paid, or deliberately closed)
 * — not before, so that a failure to *open* leaves the link intact and a plain
 * reload is the retry. `replaceState` rather than a router navigation: the
 * params were never a route, and this must not add a history entry that Back
 * would walk into.
 */
export function clearRecoveryParams(): void {
  if (!hasDom()) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('_ptxn');
  url.searchParams.delete('_paction');
  window.history.replaceState(window.history.state, '', url);
}
